import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';
import type {
  BankDto,
  BankListDto,
  BankFilterDto,
  CreateBankRequest,
  UpdateBankRequest,
  BankBranchDto,
  BankBranchListDto,
  BankBranchFilterDto,
  CreateBankBranchRequest,
  UpdateBankBranchRequest,
  BankAccountDto,
  BankAccountListDto,
  BankAccountFilterDto,
  CreateBankAccountRequest,
  UpdateBankAccountRequest,
} from '@/types/bank.types';

export const bankApi = {
  /** GET /api/v1/banks — paginated list with optional search/filter */
  getList: (filter?: BankFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<BankListDto>>>('/banks', {
      params: filter,
    }),

  /** GET /api/v1/banks/{id} — full bank record */
  getById: (id: string) =>
    apiClient.get<ApiResponse<BankDto>>(`/banks/${id}`),

  /** GET /api/v1/banks/by-code/{code} — returns 404 when not found */
  getByCode: (code: string) =>
    apiClient.get<ApiResponse<BankDto>>(
      `/banks/by-code/${encodeURIComponent(code)}`,
    ),

  /** POST /api/v1/banks — create a new bank */
  create: (data: CreateBankRequest) =>
    apiClient.post<ApiResponse<{ id: string }>>('/banks', data),

  /** PUT /api/v1/banks/{id} — update bank details */
  update: (id: string, data: UpdateBankRequest) =>
    apiClient.put<ApiResponse<object>>(`/banks/${id}`, data),

  /** DELETE /api/v1/banks/{id} — soft-delete the bank */
  remove: (id: string) =>
    apiClient.delete<ApiResponse<object>>(`/banks/${id}`),
};

export const bankBranchApi = {
  /** GET /api/v1/bank-branches — paginated list with optional bankId/search/filter */
  getList: (filter?: BankBranchFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<BankBranchListDto>>>('/bank-branches', {
      params: filter,
    }),

  /** GET /api/v1/bank-branches/{id} — full bank branch record */
  getById: (id: string) =>
    apiClient.get<ApiResponse<BankBranchDto>>(`/bank-branches/${id}`),

  /** GET /api/v1/bank-branches/by-code — returns 404 when not found */
  getByCode: (bankId: string, branchCode: string) =>
    apiClient.get<ApiResponse<BankBranchDto>>('/bank-branches/by-code', {
      params: { bankId, branchCode: encodeURIComponent(branchCode) },
    }),

  /** POST /api/v1/bank-branches — create a new bank branch */
  create: (data: CreateBankBranchRequest) =>
    apiClient.post<ApiResponse<{ id: string }>>('/bank-branches', data),

  /** PUT /api/v1/bank-branches/{id} — update bank branch details */
  update: (id: string, data: UpdateBankBranchRequest) =>
    apiClient.put<ApiResponse<object>>(`/bank-branches/${id}`, data),

  /** DELETE /api/v1/bank-branches/{id} — soft-delete the bank branch */
  remove: (id: string) =>
    apiClient.delete<ApiResponse<object>>(`/bank-branches/${id}`),
};

export const bankAccountApi = {
  /** GET /api/v1/bank-accounts — paginated list with optional search/filter */
  getList: (filter?: BankAccountFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<BankAccountListDto>>>('/bank-accounts', {
      params: filter,
    }),

  /** GET /api/v1/bank-accounts/{id} — full bank account record */
  getById: (id: string) =>
    apiClient.get<ApiResponse<BankAccountDto>>(`/bank-accounts/${id}`),

  /** POST /api/v1/bank-accounts — create a new bank account */
  create: (data: CreateBankAccountRequest) =>
    apiClient.post<ApiResponse<{ id: string }>>('/bank-accounts', data),

  /** PUT /api/v1/bank-accounts/{id} — update bank account details */
  update: (id: string, data: UpdateBankAccountRequest) =>
    apiClient.put<ApiResponse<object>>(`/bank-accounts/${id}`, data),

  /** DELETE /api/v1/bank-accounts/{id} — soft-delete the bank account */
  remove: (id: string) =>
    apiClient.delete<ApiResponse<object>>(`/bank-accounts/${id}`),
};
